/**
 * useWarrant(ticker) — fetch a warrant doc from `/api/warrants/{ticker}`.
 * Returns { warrant, isWarrant, loading } — `isWarrant` is a boolean the
 * caller can wire directly into a badge/panel conditional without
 * having to null-check warrant every time.
 *
 * Round P140 (Feb 2026) — companion to services/warrants_pipeline.py.
 * A 404 is the expected "not a warrant" signal so we deliberately
 * SWALLOW it and return `isWarrant: false` — no error toast.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useWarrant(ticker) {
  const [warrant, setWarrant] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ticker) {
      setWarrant(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/warrants/${encodeURIComponent(ticker)}`)
      .then(({ data }) => {
        if (!cancelled) setWarrant(data?.warrant || null);
      })
      .catch(() => {
        // 404 == not a warrant; treat as the "no data" signal.
        if (!cancelled) setWarrant(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [ticker]);

  return { warrant, isWarrant: !!warrant, loading };
}
